// src/lib/google-ads.ts
// Google Ads (gtag.js) conversion tracking.
//
// The base Google tag lives in index.html and loads on every route. This module
// fires the individual conversion events from the exact code point where each
// conversion happens. In an SPA you must NOT paste Google's per-conversion
// snippet into the shared <head> — it would fire on every page and over-count.
//
// Enhanced conversions: we pass plain email/phone via gtag('set', 'user_data').
// gtag normalizes and hashes them (SHA-256) client-side before sending, as long
// as enhanced conversions is enabled for the conversion action in Google Ads.

import { normalizePhone } from "@/lib/phone";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

// Account-level Google tag id. Public and stable. Overridable via env.
const GOOGLE_ADS_ID =
  (import.meta.env.VITE_GOOGLE_ADS_ID as string | undefined) || "AW-18244832497";

// Per-conversion labels. These are public (Google has you paste them into the
// page HTML). Sign-up and Activation are live; Purchase stays empty until that
// conversion action is created in Google Ads and a label supplied.
//
// Activation maps to the Google Ads "Created Menu" conversion action: it fires
// when a restaurant builds a real menu (its first item), which is the true aha
// moment — NOT the later paywalled publish step. Override via
// VITE_GOOGLE_ADS_LABEL_ACTIVATION if the action's label differs.
const LABELS: Record<ConversionType, string> = {
  signup:
    (import.meta.env.VITE_GOOGLE_ADS_LABEL_SIGNUP as string | undefined) ||
    "QHxWCKTtscAcEPGZ6PtD",
  purchase:
    (import.meta.env.VITE_GOOGLE_ADS_LABEL_PURCHASE as string | undefined) || "",
  activation:
    (import.meta.env.VITE_GOOGLE_ADS_LABEL_ACTIVATION as string | undefined) ||
    "Ek0FCIbw6sMcEPGZ6PtD",
  begin_checkout:
    (import.meta.env.VITE_GOOGLE_ADS_LABEL_BEGIN_CHECKOUT as string | undefined) ||
    "xEO7CLLMsMUcEPGZ6PtD",
};

type ConversionType = "signup" | "purchase" | "activation" | "begin_checkout";

// The Google Ads account is billed in NOK, so the nominal value on signup/lead
// uses NOK to match. Purchases report their real BRL amount and Google converts.
const ACCOUNT_CURRENCY = "NOK";

interface UserData {
  email?: string;
  phone?: string;
}

interface ConversionOptions {
  value?: number;
  currency?: string;
  transactionId?: string;
  user?: UserData;
}

const gtagReady = (): boolean =>
  typeof window !== "undefined" && typeof window.gtag === "function";

/**
 * Supply user-provided data for enhanced conversions. gtag hashes it before it
 * leaves the browser. Phone is normalized to E.164 (+55…). Partial data is fine.
 */
function setUserData({ email, phone }: UserData) {
  if (!gtagReady()) return;
  const userData: Record<string, string> = {};
  if (email) userData.email = email.trim().toLowerCase();
  if (phone) {
    const digits = normalizePhone(phone); // "55…" digits only
    if (digits) userData.phone_number = `+${digits}`;
  }
  if (Object.keys(userData).length === 0) return;
  window.gtag!("set", "user_data", userData);
}

function fireConversion(type: ConversionType, opts: ConversionOptions = {}) {
  if (!gtagReady()) return;

  const label = LABELS[type];
  if (!label) {
    // Conversion action not created yet (no label). No-op so nothing breaks.
    return;
  }

  if (opts.user) setUserData(opts.user);

  const params: Record<string, unknown> = {
    send_to: `${GOOGLE_ADS_ID}/${label}`,
  };
  if (opts.value !== undefined) {
    params.value = opts.value;
    params.currency = opts.currency ?? ACCOUNT_CURRENCY;
  }
  if (opts.transactionId) params.transaction_id = opts.transactionId;

  window.gtag!("event", "conversion", params);
}

/**
 * Sign-up: a real account was created (OTP verified, which also confirms the
 * email). This is the primary conversion to optimize Google Ads toward.
 */
export function trackGoogleSignup(user?: UserData, transactionId?: string) {
  // value/currency match the snippet Google generated for this conversion action.
  // transactionId (the user id) is a stable dedupe key: Google reconciles this
  // browser hit with any future server-side / offline import that shares it, and
  // it guards against an accidental refire counting twice.
  fireConversion("signup", { value: 1.0, currency: ACCOUNT_CURRENCY, user, transactionId });
}

/**
 * Purchase: a paid subscription became active. Reports the real BRL value when
 * known so Google can compute ROAS (it converts BRL to the account currency).
 */
export function trackGooglePurchase(opts: {
  value?: number;
  transactionId?: string;
  user?: UserData;
}) {
  fireConversion("purchase", {
    value: opts.value,
    currency: "BRL",
    transactionId: opts.transactionId,
    user: opts.user,
  });
}

/**
 * Activation: the restaurant created a real menu (its first item) — the
 * product's "aha" moment, fired once per restaurant via the claim_menu_created()
 * guard (see src/lib/menu-created.ts), NOT the later paywalled publish step.
 * Maps to the Google Ads "Created Menu" action. A mid-funnel engagement signal;
 * keep it observation-only in Google Ads so it doesn't disturb Sign-up bidding.
 * Same enhanced-conversions + dedupe contract as Sign-up: pass the user's
 * email/phone (gtag hashes them) and the user id as a stable transaction id.
 */
export function trackGoogleActivation(user?: UserData, transactionId?: string) {
  fireConversion("activation", { value: 1.0, currency: ACCOUNT_CURRENCY, user, transactionId });
}

/**
 * Begin checkout: the user started a Stripe Checkout (a checkout session was
 * created and we are about to redirect), one step before Purchase. A mid-funnel
 * intent signal — keep it observation-only (Secondary) in Google Ads so it does
 * not disturb Sign-up / Purchase bidding. Pass the user's email (gtag hashes it)
 * and a per-click id as the dedup transaction id. Unlike signup/activation, do NOT
 * pass the user id here: begin-checkout can recur, and a random per-click id keeps
 * our internal id off the ad network while still collapsing accidental double-fires.
 */
export function trackGoogleBeginCheckout(user?: UserData, transactionId?: string) {
  fireConversion("begin_checkout", { value: 1.0, currency: ACCOUNT_CURRENCY, user, transactionId });
}
